import { DOCUMENT, Location } from "@angular/common";
import { DestroyRef, inject, Injectable } from "@angular/core";
import { CanDeactivateFn, RedirectCommand, Router } from "@angular/router";
import { isPaymentCancellation, PaymentCancellationCode } from "../domain/payment.constants";

const VenmoResultKeys = ["venmoSuccess", "venmoCancel", "venmoError"] as const;
const BrowserNavigationTrigger = "popstate";
const DiagnosticPrefix = "[Venmo return]";
const DiagnosticEvent = {
  Started: "authorization-started",
  Callback: "callback-observed",
  Preserved: "checkout-preserved",
  Succeeded: "sdk-succeeded",
  Canceled: "sdk-canceled",
  Failed: "sdk-failed",
  Cleaned: "shareable-url-restored",
  Destroyed: "checkout-destroyed",
  Hidden: "page-hidden",
  Visible: "page-visible",
} as const;

interface VenmoAttempt {
  shareableUrl: string;
  returnTarget: string;
  checkoutUrl: string;
  settled: boolean;
  callbackObserved: boolean;
}

/** Owns navigation metadata only. Payment payloads remain in PaymentSdkService. */
@Injectable({ providedIn: "root" })
export class VenmoReturnService {
  private readonly _location = inject(Location);
  private readonly _router = inject(Router);
  private readonly _document = inject(DOCUMENT);
  private readonly _destroyRef = inject(DestroyRef);
  private _attempt: VenmoAttempt | null = null;

  constructor() {
    const subscription = this._location.subscribe(event => {
      const attempt = this._attempt;
      if (attempt && this._isCallback(event.url, attempt)) {
        this._observeCallback(attempt);
        // Polling can finish before the browser delivers its callback event.
        this._cleanReturnUrl(attempt);
      }
    });
    const onVisibilityChange = () => {
      if (this._attempt && !this._attempt.settled) {
        this._log(this._document.hidden ? DiagnosticEvent.Hidden : DiagnosticEvent.Visible);
      }
    };
    this._document.addEventListener("visibilitychange", onVisibilityChange);
    this._destroyRef.onDestroy(() => {
      subscription.unsubscribe();
      this._document.removeEventListener("visibilitychange", onVisibilityChange);
    });
  }

  async tokenize<T>(tokenize: () => Promise<T>): Promise<T> {
    if (this._attempt && !this._attempt.settled) {
      throw new Error("Venmo authorization is already in progress.");
    }
    const browserUrl = this._location.path(true);
    const parsed = this._parse(browserUrl);
    const attempt: VenmoAttempt = {
      shareableUrl: parsed.isResult ? parsed.target : browserUrl,
      returnTarget: parsed.target,
      checkoutUrl: this._withoutResult(this._router.url),
      settled: false,
      callbackObserved: false,
    };
    this._attempt = attempt;
    this._log(DiagnosticEvent.Started);
    if (parsed.isResult) this._observeCallback(attempt);

    try {
      // Invoke before the first await so the SDK retains the button gesture.
      const payload = await tokenize();
      if (this._attempt !== attempt) {
        throw Object.assign(new Error("The Venmo checkout was closed."), {
          code: PaymentCancellationCode.VenmoCanceled,
        });
      }
      this._log(DiagnosticEvent.Succeeded);
      return payload;
    } catch (error) {
      if (this._attempt === attempt) {
        this._log(isPaymentCancellation(error) ? DiagnosticEvent.Canceled : DiagnosticEvent.Failed);
      }
      throw error;
    } finally {
      attempt.settled = true;
      this._cleanReturnUrl(attempt);
      // Retain this small record until the next attempt or checkout teardown.
      // Angular defers popstate processing; clearing here races with its guard.
    }
  }

  protectNavigation(currentUrl: string, nextUrl: string): true | RedirectCommand {
    const attempt = this._attempt;
    if (!attempt || this._router.currentNavigation()?.trigger !== BrowserNavigationTrigger ||
        this._withoutResult(currentUrl) !== attempt.checkoutUrl || !this._isCallback(nextUrl, attempt)) {
      return true;
    }

    this._observeCallback(attempt);
    this._cleanReturnUrl(attempt);
    this._log(DiagnosticEvent.Preserved);
    // Returning false would let Angular restore the internal /donate URL.
    return new RedirectCommand(this._router.parseUrl(attempt.checkoutUrl), {
      skipLocationChange: true,
    });
  }

  clear(): void {
    if (this._attempt) this._log(DiagnosticEvent.Destroyed);
    this._attempt = null;
  }

  private _cleanReturnUrl(attempt: VenmoAttempt): void {
    if (this._attempt !== attempt || !attempt.settled ||
        !this._isCallback(this._location.path(true), attempt)) {
      return;
    }
    // Replace only our callback, after SDK consumption; preserve Angular's state.
    // This does not emit a browser popstate/hashchange or navigate the router.
    this._location.replaceState(attempt.shareableUrl, "", this._location.getState());
    this._log(DiagnosticEvent.Cleaned);
  }

  private _isCallback(url: string, attempt: VenmoAttempt): boolean {
    const parsed = this._parse(url);
    return parsed.isResult && parsed.target === attempt.returnTarget;
  }

  private _withoutResult(url: string): string {
    const parsed = this._parse(url);
    return parsed.isResult ? parsed.target : url;
  }

  private _parse(url: string): { target: string; isResult: boolean } {
    const tree = this._router.parseUrl(url);
    const fragment = new URLSearchParams(tree.fragment || "");
    const isResult = VenmoResultKeys.some(key => fragment.get(key) === "1");
    tree.fragment = null;
    return { target: this._router.serializeUrl(tree), isResult };
  }

  private _observeCallback(attempt: VenmoAttempt): void {
    if (!attempt.callbackObserved) {
      attempt.callbackObserved = true;
      this._log(DiagnosticEvent.Callback);
    }
  }

  private _log(event: (typeof DiagnosticEvent)[keyof typeof DiagnosticEvent]): void {
    // Deployment diagnostics intentionally contain no URL, token, nonce or donor data.
    console.info(DiagnosticPrefix, event);
  }
}

export const preserveVenmoCheckout: CanDeactivateFn<unknown> = (_component, _route, current, next) =>
  inject(VenmoReturnService).protectNavigation(current.url, next.url);
